/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { guardCall } from '../lib/officeHours.js'
import { useEffect, useRef, useState } from 'react'
import { IcPhone, IcTelegram, IcWhatsapp, IcYoutube, IcAparat, IcInstagram, HeaderLoader } from './icons.jsx'
import NavBar from './NavBar.jsx'
import Account from './Account.jsx'

const WA_TEXT = 'https://wa.me/989195550535?text=سلام%20وقتتون%20بخیر.%20می%E2%80%8Cخواستم%20در%20مورد%20کلاس%E2%80%8Cهای%20حضوریتون%20مشاوره%20بگیرم'

const SOCIALS = [
  { label: 'Telegram', href: 'https://t.me/sequoiafitness_channel', Icon: IcTelegram },
  { label: 'WhatsApp', href: WA_TEXT, Icon: IcWhatsapp },
  { label: 'YouTube', href: 'https://www.youtube.com/@Sequoia.fitness', Icon: IcYoutube },
  { label: 'Aparat', href: 'https://www.aparat.com/sequoiagym', Icon: IcAparat },
  { label: 'Instagram', href: 'https://instagram.com/app_sequoia', Icon: IcInstagram },
]

function useStickyHeader(topRef, headRef) {
  const [stuck, setStuck] = useState(false)
  useEffect(() => {
    const top = topRef.current
    const head = headRef.current
    const footer = document.getElementById('seq-footer')
    if (!top || !head || !footer) return undefined
    let footerTop = 0
    let limit = 0
    let tick = 0
    const measure = () => {
      const bar = top.firstElementChild
      const bs = getComputedStyle(bar)
      const hs = getComputedStyle(head)
      const total =
        bar.offsetHeight + parseFloat(bs.marginTop) + parseFloat(bs.marginBottom) + head.offsetHeight + parseFloat(hs.marginTop) + parseFloat(hs.marginBottom)
      top.style.minHeight = total + 'px'
      footerTop = footer.getBoundingClientRect().top + window.scrollY
      limit = 20 + head.offsetHeight
    }
    const update = () => {
      tick = 0
      setStuck(footerTop - window.scrollY <= limit ? false : window.scrollY > 55)
    }
    const onScroll = () => {
      if (!tick) tick = requestAnimationFrame(update)
    }
    const onResize = () => {
      measure()
      update()
    }
    measure()
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)
    window.addEventListener('load', onResize)
    const ro = new ResizeObserver(onResize)
    ro.observe(document.body)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('load', onResize)
      ro.disconnect()
      if (tick) cancelAnimationFrame(tick)
    }
  }, [topRef, headRef])
  return stuck
}

export default function Header() {
  const topRef = useRef(null)
  const headRef = useRef(null)
  const stuck = useStickyHeader(topRef, headRef)
  const [touchExpanded, setTouchExpanded] = useState(-1)
  const justExpandedRef = useRef(false)

  useEffect(() => {
    const collapse = () => {
      if (justExpandedRef.current) { justExpandedRef.current = false; return }
      setTouchExpanded(-1)
    }
    document.addEventListener('touchstart', collapse, { passive: true })
    return () => document.removeEventListener('touchstart', collapse)
  }, [])

  function handleSocialClick(e, i, href) {
    if (e.pointerType !== 'touch') return
    if (touchExpanded !== i) {
      e.preventDefault()
      justExpandedRef.current = true
      setTouchExpanded(i)
    }
  }

  return (
    <div className="lp-top lp-root" ref={topRef}>
      <div className="lp-bar" data-fx="bar">
        <div className="lp-bar-l">
          <div className="lp-bar-phone top-icon-holder-right">
            <a className="top-right-head" href="tel:02126254146" onClick={guardCall}>
              <span className="call-time">تماس از 9 صبح تا 5 بعد از ظهر</span> <span>46 41 25 26 021</span> <IcPhone className="lp-bar-phone-svg" />
            </a>
          </div>
        </div>
        <div className="lp-bar-r">
          {SOCIALS.map(({ label, href, Icon }, i) => (
            <div
              className={(i === SOCIALS.length - 1 ? 'lp-bar-ico-last' : 'lp-bar-ico') + ' top-icon-holder' + (touchExpanded === i ? ' lp-bar-ico-touch-open' : '')}
              key={label}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <div className="lp-bar-ico-in">
                <a href={href} target="_blank" rel="noreferrer" onClick={(e) => handleSocialClick(e, i, href)}>
                  <span>{label}</span> <Icon className="lp-bar-svg" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={'lp-head seq-header' + (stuck ? ' seq-head-stick' : '')} id="seq-header" ref={headRef}>
        <div className="lp-head-row">
          <div className="lp-head-nav">
            <NavBar />
          </div>
          <div className="lp-head-acct">
            <Account />
          </div>
        </div>
        <div className="lp-head-logo">
          <a href="/" aria-label="سکویا فیتنس؛ صفحه اصلی">
            <div className="logo-mask"></div>
          </a>
        </div>
        <div className="lp-head-curve">
          <div className="lp-head-curve-in">
            <div className="lp-head-loader">
              <div id="header-loader">
                <HeaderLoader className="lp-head-loader-svg" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
